/**
 * Skeleton — shared pulsing-rectangle placeholder primitive.
 *
 * Plan §"Pre-execution gap closure" Gap 5 — every loading state in the
 * v2 add-in renders a Skeleton instead of the framework default.
 * Honors `prefers-reduced-motion` via Tailwind's `motion-safe:` variant
 * so users with the reduced-motion preference see a still rectangle.
 *
 * Owners: Agent C (catalog/KPI/client tile), Agent D (instance lists,
 * artifacts), Agent B (HITL drawer form). Single primitive — every
 * agent imports from here.
 *
 * Usage:
 *   <Skeleton width="100%" height={48} />
 *   <Skeleton width={120} height={20} className="rounded-md" />
 */
import type { CSSProperties } from "react";

export interface SkeletonProps {
  /** CSS width — number = px, string = literal CSS value. */
  width?: number | string;
  /** CSS height — number = px, string = literal CSS value. */
  height?: number | string;
  /**
   * Optional override classes. Defaults provide rounded corners and
   * the brand-aligned pulse background. Caller can override or extend
   * (e.g., add `rounded-full` for a circular placeholder).
   */
  className?: string;
  /** ARIA label for screen readers; defaults to "Loading". */
  ariaLabel?: string;
}

function toCssDim(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value === "number") return `${value}px`;
  return value;
}

export default function Skeleton({
  width,
  height,
  className,
  ariaLabel = "Loading",
}: SkeletonProps) {
  const style: CSSProperties = {};
  const w = toCssDim(width);
  const h = toCssDim(height);
  if (w) style.width = w;
  if (h) style.height = h;

  // motion-safe: → animation only runs when prefers-reduced-motion is NOT
  // requested. Reduced-motion users get a still rectangle, which still
  // communicates "something is loading here" via shape.
  const baseClasses =
    "bg-primary-gray-light-100 rounded motion-safe:animate-pulse";
  const classes = className ? `${baseClasses} ${className}` : baseClasses;

  return (
    <div
      role="status"
      aria-label={ariaLabel}
      aria-busy="true"
      className={classes}
      style={style}
    />
  );
}
